import React, { useState, useEffect, useRef } from 'react';
import { Box, Slider } from '@mantine/core';
import { useModule } from '@/hooks/useModule';
import {DBObjects} from '@/store/store_types';

interface AudioSeekBarProps {
    showTimeLabels?: boolean;
    fileInfo?: DBObjects['File'];
}

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioSeekBar({ showTimeLabels = true, fileInfo }: AudioSeekBarProps) {
    const audioPlayerModule = useModule('AudioPlayer');
    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();

    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragValue, setDragValue] = useState(0);
    const intervalRef = useRef<number | null>(null);

    const isCurrentlyPlaying = fileInfo ? playbackStatus?.fileInfo?.id === fileInfo?.id : Boolean(playbackStatus?.fileInfo);

    useEffect(() => {
        if (isCurrentlyPlaying && playbackStatus?.isPlaying && !isDragging) {
            intervalRef.current = window.setInterval(() => {
                const audio = (window as any).audioElement as HTMLAudioElement | undefined;
                if (audio) {
                    setCurrentTime(audio.currentTime);
                    setDuration(audio.duration || 0);
                }
            }, 100);
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isCurrentlyPlaying, playbackStatus?.isPlaying, isDragging]);

    const handleSliderChange = (value: number) => {
        if (!isCurrentlyPlaying) return;
        setIsDragging(true);
        setDragValue(value);
    };

    const handleSliderChangeEnd = (value: number) => {
        if (!isCurrentlyPlaying) return;

        const audio = (window as any).audioElement as HTMLAudioElement | undefined;
        if (audio && duration) {
            const newTime = (value / 100) * duration;
            audio.currentTime = newTime;
            setCurrentTime(newTime);
        }
        setIsDragging(false);
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
    const displayProgress = isDragging ? dragValue : progress;

    return (
        <Box style={{ width: '100%' }}>
            <Slider
                value={displayProgress}
                onChange={handleSliderChange}
                onChangeEnd={handleSliderChangeEnd}
                label={null}
                disabled={!isCurrentlyPlaying}
                marks={showTimeLabels ? [
                    { value: 0, label: formatTime(currentTime) },
                    { value: 100, label: formatTime(duration) },
                ] : undefined}
                styles={{
                    markLabel: { marginTop: 8 },
                }}
            />
        </Box>
    );
}
